import React, { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'
import { Spinner } from './Spinner'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'accent'
  /** Size — all sizes meet 44×44 min touch target */
  size?: 'sm' | 'md' | 'lg'
  /** Shows spinner and disables interaction */
  loading?: boolean
  /** Optional icon rendered before the label */
  leftIcon?: React.ReactNode
  /** Optional icon rendered after the label */
  rightIcon?: React.ReactNode
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: [
    'bg-primary-600 text-white',
    // Hover: subtle gradient overlay
    'hover:bg-gradient-to-br hover:from-primary-600 hover:to-primary-700',
    // Active: 8% darker
    'active:bg-primary-700 active:from-primary-700 active:to-primary-800',
    // Focus
    'focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
  ].join(' '),

  secondary: [
    'bg-white text-primary-700 border border-primary-200',
    'hover:bg-primary-50 hover:border-primary-300',
    'active:bg-primary-100',
    'focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
  ].join(' '),

  ghost: [
    'bg-transparent text-neutral-700',
    'hover:bg-neutral-100',
    'active:bg-neutral-200',
    'focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
  ].join(' '),

  destructive: [
    'bg-error-500 text-white',
    'hover:bg-red-600',
    'active:bg-red-700',
    'focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2',
  ].join(' '),

  accent: [
    'bg-accent-500 text-white',
    'hover:bg-accent-600',
    'active:bg-accent-600',
    'focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2',
  ].join(' '),
}

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'min-h-[44px] min-w-[44px] px-3 py-2 text-sm gap-1.5',
  md: 'min-h-[44px] min-w-[44px] px-4 py-2.5 text-sm gap-2',
  lg: 'min-h-[44px] min-w-[44px] px-6 py-3 text-base gap-2.5',
}

/**
 * Design-system Button — ARCHITECTURE.md §17 compliant.
 *
 * - All sizes enforce 44×44px minimum touch target (WCAG 2.5.5)
 * - Loading state keeps width stable via invisible children + absolute spinner
 * - Hover: 4% darken; Active: 8% darken; Primary CTA gets gradient on hover
 * - Focus ring uses `focus-visible` to avoid showing on mouse click
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      className,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        className={cn(
          // Base styles
          'relative inline-flex items-center justify-center',
          'rounded font-medium leading-none',
          'select-none whitespace-nowrap',
          'transition-colors duration-150 ease-in-out',
          // Variant + size
          variantClasses[variant],
          sizeClasses[size],
          // Disabled styles
          isDisabled && 'pointer-events-none opacity-50',
          className,
        )}
        {...props}
      >
        {/* Invisible children to maintain width during loading */}
        <span
          className={cn(
            'inline-flex items-center gap-inherit',
            loading && 'invisible',
          )}
        >
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="shrink-0">{rightIcon}</span>}
        </span>

        {/* Absolutely positioned spinner to overlay */}
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner
              size={size === 'lg' ? 20 : 16}
              aria-label="Laddar"
            />
          </span>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
